import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { GET_CUSTOM_WORKLIST } from 'modules/Apollo/queries';
import type { GetCustomWorklistQuery } from 'generated/graphql';
import { logger } from 'modules/logger';

import { activeCustomWorklistFiltersState } from '../state';
import { useCustomWorklistLoadingContext } from '../contexts/CustomWorklistLoadingContext';
import { useGenerateFilterConfigV2 } from '../../WorklistFilters/hooks/useGenerateFilterConfigV2';
import { useSavedSortingPreferences } from './useSavedSortingPreferences';
import { useSaveLastViewedCustomWorklists } from './useLastViewedCustomWorklists';
import { useCustomWorklists } from './useCustomWorklists';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useIsFilterPersistenceDisabled } from './useIsFilterPersistenceDisabled';

import { formatCustomWorklistFilters } from '../WorklistPageV2/utils';
import { getWorklistKey } from '../utils/worklistUtils';
import { isEmptyStringOrNull } from 'utils/string';
import {
  getWorklistURLParams,
  parseFiltersFromURLParams,
  convertWorklistFiltersToState,
} from '../../WorklistFilters/utils';
import { WorklistFilters } from '../../WorklistFilters/types';
import type { FilterStateType } from '../../WorklistFilters/types';
import { WorklistSurface } from '../types';
import { WithApolloContext } from 'modules/Apollo/types';

type FiltersMap = FilterStateType;

/**
 * Hook to load and update custom worklist filters.
 *
 * The load functions accept explicit worklist IDs which are used to fetch the correct preferences.
 * This ensures preferences are always sourced from the new preference store, even on initial loads.
 *
 * @param replaceFilters - Function to replace the current filter state
 * @returns Object with load functions and loading state
 */
export const useUpdateCustomWorklistFilters = ({
  replaceFilters,
  context,
  skipQueries = false,
}: {
  replaceFilters: (filters: FilterStateType) => void;
  skipQueries?: boolean;
} & WithApolloContext) => {
  const { savedSortingPreferences } = useSavedSortingPreferences({
    context,
    skipQuery: skipQueries,
  });
  const { isPublicOrCombinedWorklist, allWorklists } = useCustomWorklists({
    queryOptions: { context, skip: skipQueries },
  });
  const { setLoadingRefs, clearLoadingRefs, updateLoadingRefs, currentRequestRef } =
    useCustomWorklistLoadingContext();

  const { availableFilters } = useGenerateFilterConfigV2({ context, skipQueries });
  const { data: currentUserData } = useCurrentUser({ context, skip: skipQueries });
  const isFilterPersistenceDisabled = useIsFilterPersistenceDisabled({
    context,
    skip: skipQueries,
  });
  const setActiveCustomWorklistFilters = useSetRecoilState(activeCustomWorklistFiltersState);

  const getFilterPreferencesForIDs = useCallback(
    (worklistIds: string[]): FilterStateType | null => {
      // Persistence off: the worklist should render only its definition filters.
      if (isFilterPersistenceDisabled) {
        return null;
      }
      if (!currentUserData?.me || worklistIds.length === 0) {
        return null;
      }

      const isPublic = isPublicOrCombinedWorklist(worklistIds);

      if (isPublic) {
        // For public/combined worklists - get preferences from user's worklistSettings
        const key = getWorklistKey(worklistIds);
        const preference = currentUserData.me.worklistSettings?.worklistPreferences?.find(
          (pref) => pref?.worklistKey === key
        );

        if (preference?.filters && preference.filters.length > 0) {
          const validFilters = preference.filters
            .filter(
              (filter) =>
                filter != null &&
                !isEmptyStringOrNull(filter.name) &&
                filter.values &&
                filter.values.length > 0
            )
            .map((filter) => ({
              name: filter!.name,
              values: filter!.values.filter((v): v is string => v != null),
            }))
            .filter((filter) => filter.values.length > 0);

          if (validFilters.length > 0) {
            return convertWorklistFiltersToState(validFilters);
          }
        }
      } else {
        // For private worklists - get preferences from the private worklist's advancedSettings
        const worklistId = worklistIds[0];
        const worklist = allWorklists.get(worklistId);
        const advancedSettings = worklist?.advancedSettings;

        if (advancedSettings && 'worklistPreferences' in advancedSettings) {
          const filters = advancedSettings.worklistPreferences?.filters;

          if (filters && filters.length > 0) {
            const validFilters = filters
              .filter(
                (filter): filter is { name: string; values: readonly string[] } =>
                  filter != null &&
                  !isEmptyStringOrNull(filter.name) &&
                  !!filter.values &&
                  filter.values.length > 0
              )
              .map((filter) => ({
                name: filter.name,
                values: [...filter.values],
              }))
              .filter((filter) => filter.values.length > 0);

            if (validFilters.length > 0) {
              return convertWorklistFiltersToState(validFilters);
            }
          }
        }
      }

      return null;
    },
    [currentUserData, isPublicOrCombinedWorklist, allWorklists, isFilterPersistenceDisabled]
  );
  const { saveLastViewedCustomWorklists } = useSaveLastViewedCustomWorklists({ context });

  const [getCustomWorklistDetails, { loading: areCustomWorklistFiltersLoading }] =
    useLazyQuery<GetCustomWorklistQuery>(GET_CUSTOM_WORKLIST, { context });

  const applyFilters = useCallback(
    (filters: FiltersMap) => {
      replaceFilters(filters);
    },
    [replaceFilters]
  );

  const loadSingle = useCallback(
    async (customWorklistID: string): Promise<Nullable<FilterStateType>> => {
      if (customWorklistID == null) {
        logger.warn('[useUpdateCustomWorklistFilters] Custom worklists not enabled or invalid ID');
        return null;
      }

      const requestKey = customWorklistID;

      logger.info(
        `[useUpdateCustomWorklistFilters] Loading single custom worklist: ${customWorklistID}`
      );

      setLoadingRefs(requestKey);

      let filtersToApply: FiltersMap = {};

      try {
        // Use network-only for first load to ensure fresh data and avoid stale cache
        const { data } = await getCustomWorklistDetails({
          variables: { smid: customWorklistID },
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'cache-first',
        });

        if (currentRequestRef.current !== requestKey) {
          logger.info(
            `[useUpdateCustomWorklistFilters] Request cancelled for ${customWorklistID}, newer request in progress`
          );
          return null;
        }

        const fetchedFilters = formatCustomWorklistFilters(data?.customWorklist?.filters || []);
        filtersToApply = fetchedFilters;

        // Prioritize new custom worklist filter preferences over legacy saved preferences
        const customWorklistFilterPreferences = getFilterPreferencesForIDs([customWorklistID]);
        if (
          customWorklistFilterPreferences &&
          Object.keys(customWorklistFilterPreferences).length > 0
        ) {
          filtersToApply = customWorklistFilterPreferences;
          logger.info(
            `[useUpdateCustomWorklistFilters] Applied custom worklist filter preferences for ${customWorklistID}`,
            { filters: filtersToApply }
          );
        } else if (isFilterPersistenceDisabled) {
          logger.info(
            `[useUpdateCustomWorklistFilters] Persistence disabled for ${customWorklistID}, using worklist definition`,
            { filters: filtersToApply }
          );
        } else {
          const saved =
            savedSortingPreferences[`${WorklistSurface.CUSTOM_WORKLIST}_${customWorklistID}`];

          if (saved && availableFilters != null) {
            const { filters } = getWorklistURLParams(new URLSearchParams(saved));
            const parsedFilters =
              filters != null
                ? (parseFiltersFromURLParams(filters as any, availableFilters) as FiltersMap)
                : {};

            if (Object.keys(parsedFilters).length > 0) {
              filtersToApply = parsedFilters;
              logger.info(
                `[useUpdateCustomWorklistFilters] Applied legacy saved preferences for ${customWorklistID}`,
                { filters: filtersToApply }
              );
            } else {
              logger.info(
                `[useUpdateCustomWorklistFilters] Saved preferences empty for ${customWorklistID}, using worklist definition`,
                { filters: filtersToApply }
              );
            }
          } else {
            logger.info(
              `[useUpdateCustomWorklistFilters] No saved preferences for ${customWorklistID}, using worklist definition`,
              { filters: filtersToApply }
            );
          }
        }

        // Ensure activeCustomWorklistFilters always has examStatus from the worklist definition
        const activeFilters = {
          ...fetchedFilters,
          examStatus: fetchedFilters?.examStatus || filtersToApply?.examStatus || [],
        };

        applyFilters(filtersToApply);
        setActiveCustomWorklistFilters(activeFilters);

        saveLastViewedCustomWorklists([
          {
            surface: WorklistSurface.CUSTOM_WORKLIST,
            worklistViewID: [customWorklistID],
          },
        ]);

        logger.info(
          `[useUpdateCustomWorklistFilters] Successfully loaded custom worklist ${customWorklistID}`,
          { filters: filtersToApply }
        );

        return filtersToApply;
      } catch (error) {
        logger.error(
          `[useUpdateCustomWorklistFilters] Error loading custom worklist ${customWorklistID}`,
          error
        );
        return null;
      } finally {
        clearLoadingRefs(requestKey);
      }
    },
    [
      setLoadingRefs,
      applyFilters,
      getCustomWorklistDetails,
      currentRequestRef,
      getFilterPreferencesForIDs,
      isFilterPersistenceDisabled,
      setActiveCustomWorklistFilters,
      saveLastViewedCustomWorklists,
      savedSortingPreferences,
      availableFilters,
      clearLoadingRefs,
    ]
  );

  const loadCombined = useCallback(
    async (activeWorklistIds: string[]): Promise<Nullable<FilterStateType>> => {
      if (activeWorklistIds.length === 0) {
        logger.warn('[useUpdateCustomWorklistFilters] Custom worklists not enabled or empty IDs');
        return null;
      }

      const requestKey = getWorklistKey(activeWorklistIds);

      logger.info(
        `[useUpdateCustomWorklistFilters] Loading combined custom worklists: ${activeWorklistIds.join(', ')}`
      );

      setActiveCustomWorklistFilters({});
      updateLoadingRefs(requestKey);

      let filtersToApply: FiltersMap = {};

      try {
        // Prioritize new custom worklist filter preferences over legacy saved preferences
        const customWorklistFilterPreferences = getFilterPreferencesForIDs(activeWorklistIds);
        if (
          customWorklistFilterPreferences &&
          Object.keys(customWorklistFilterPreferences).length > 0
        ) {
          filtersToApply = customWorklistFilterPreferences;
          logger.info(
            `[useUpdateCustomWorklistFilters] Applied custom worklist filter preferences for combined worklist ${requestKey}`,
            { filters: filtersToApply }
          );
        } else if (isFilterPersistenceDisabled) {
          logger.info(
            `[useUpdateCustomWorklistFilters] Persistence disabled for combined worklist ${requestKey}, using views filter only`
          );
        } else {
          const savedPreferenceKey = `${WorklistSurface.CUSTOM_WORKLIST}_${requestKey}`;
          const saved = savedSortingPreferences[savedPreferenceKey];

          if (saved && availableFilters != null) {
            const { filters } = getWorklistURLParams(new URLSearchParams(saved));
            const parsedFilters =
              filters != null
                ? (parseFiltersFromURLParams(filters as any, availableFilters) as FiltersMap)
                : {};

            if (Object.keys(parsedFilters).length > 0) {
              filtersToApply = parsedFilters;
              logger.info(
                `[useUpdateCustomWorklistFilters] Applied legacy saved preferences for combined worklist ${requestKey}`,
                { filters: filtersToApply }
              );
            } else {
              logger.info(
                `[useUpdateCustomWorklistFilters] Saved preferences empty for combined worklist ${requestKey}, using views filter`
              );
            }
          } else {
            logger.info(
              `[useUpdateCustomWorklistFilters] No saved preferences for combined worklist ${requestKey}, using views filter`
            );
          }
        }

        // Always include the Views filter for combined worklists
        const finalFilters = {
          ...filtersToApply,
          [WorklistFilters.Views]: activeWorklistIds,
        };

        applyFilters(finalFilters);

        logger.info(
          `[useUpdateCustomWorklistFilters] Successfully loaded combined custom worklists: ${activeWorklistIds.join(', ')}`,
          { filters: finalFilters }
        );

        return finalFilters;
      } catch (error) {
        logger.error(
          `[useUpdateCustomWorklistFilters] Error loading combined custom worklists: ${activeWorklistIds.join(', ')}`,
          error
        );
        return null;
      } finally {
        clearLoadingRefs(requestKey);
      }
    },
    [
      applyFilters,
      availableFilters,
      clearLoadingRefs,
      getFilterPreferencesForIDs,
      isFilterPersistenceDisabled,
      savedSortingPreferences,
      setActiveCustomWorklistFilters,
      updateLoadingRefs,
    ]
  );

  return useMemo(
    () => ({
      updateSingleCustomWorklistFilters: loadSingle,
      updateCombinedCustomWorklistFilters: loadCombined,
      areCustomWorklistFiltersLoading,
    }),
    [areCustomWorklistFiltersLoading, loadCombined, loadSingle]
  );
};

export type UseUpdateCustomWorklistFiltersReturn = ReturnType<
  typeof useUpdateCustomWorklistFilters
>;
